import React, { useState, useEffect, useRef } from 'react';
import './ExportData.css';
import { Download, FileText, ChevronDown, Check, Database, Search, ArrowLeft } from 'lucide-react';

const ExportData = ({ onBack, onNavigate }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedData, setSelectedData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  
  const dropdownRef = useRef(null);
  const successTimeoutRef = useRef(null);

  // Sample data sources
  const dataSources = [
    { id: 1, name: 'Document Search Results', type: 'search', count: 15 },
    { id: 2, name: 'Metadata Extraction Results', type: 'metadata', count: 8 },
    { id: 3, name: 'HSE Audit Reports', type: 'document', count: 5 },
    { id: 4, name: 'Quality Management Records', type: 'document', count: 12 },
    { id: 5, name: 'Engineering Specifications', type: 'document', count: 23 },
    { id: 6, name: 'Production Logs', type: 'data', count: 45 },
    { id: 7, name: 'Incident Reports', type: 'report', count: 7 },
    { id: 8, name: 'Training Records', type: 'data', count: 19 },
  ];

  const formats = [
    { id: 'pdf', name: 'PDF Document', icon: <FileText size={16} />, description: 'Portable Document Format - Best for reports' },
    { id: 'csv', name: 'CSV File', icon: <Database size={16} />, description: 'Comma-separated values - For data analysis' },
    { id: 'json', name: 'JSON File', icon: <Database size={16} />, description: 'JavaScript Object Notation - For developers' },
    { id: 'excel', name: 'Excel File', icon: <FileText size={16} />, description: 'Microsoft Excel format - For spreadsheets' },
    { id: 'xml', name: 'XML File', icon: <FileText size={16} />, description: 'Extensible Markup Language - Data exchange' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFormatDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const filteredDataSources = dataSources.filter(source =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedData.length === filteredDataSources.length) {
      setSelectedData([]);
    } else {
      setSelectedData(filteredDataSources.map(s => s.id));
    }
  };

  const handleSelectSource = (id) => {
    if (selectedData.includes(id)) {
      setSelectedData(selectedData.filter(item => item !== id));
    } else {
      setSelectedData([...selectedData, id]);
    }
  };

  const handleExport = () => {
    if (selectedData.length === 0) {
      alert('Please select at least one data source to export');
      return;
    }

    setIsExporting(true);
    setExportSuccess(false);
    
    // Clear any existing timeout
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    
    // Simulate export process
    setTimeout(() => {
      setIsExporting(false);
      setExportSuccess(true);
      
      // Reset success message after 3 seconds
      successTimeoutRef.current = setTimeout(() => {
        setExportSuccess(false);
      }, 3000);
      
      console.log(`Exporting ${selectedData.length} items in ${selectedFormat} format`);
    }, 2000);
  };

  const getFormatIcon = (formatId) => {
    const format = formats.find(f => f.id === formatId);
    return format ? format.icon : <FileText size={16} />;
  };

  const getSelectedCountText = () => {
    const count = selectedData.length;
    if (count === 0) return 'No items selected';
    if (count === 1) return '1 item selected';
    return `${count} items selected`;
  };

  const selectedFormatName = formats.find(f => f.id === selectedFormat)?.name || 'Select Format';
  const totalItems = selectedData.length;
  const estimatedSize = totalItems > 0 ? `${(totalItems * 0.5).toFixed(1)} MB` : '0 MB';

  return (
    <div className="export-data-container">
      {/* Header */}
      <div className="export-data-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={14} className="back-icon" />
          <span>Back</span>
        </button>
        <h2 className="export-data-title">Export Data</h2>
      </div>

      {/* Main Content Wrapper - THIS WAS MISSING! */}
      <div className="export-data-content-wrapper">
        <div className="export-data-content">
          {/* Left Panel - Data Sources */}
          <div className="data-sources-panel">
            <div className="panel-header">
              <h3>Data Sources</h3>
              <button 
                className="select-all-btn"
                onClick={handleSelectAll}
              >
                {selectedData.length === filteredDataSources.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search data sources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="data-sources-list">
              {filteredDataSources.length > 0 ? (
                filteredDataSources.map(source => (
                  <div 
                    key={source.id}
                    className={`data-source-item ${selectedData.includes(source.id) ? 'selected' : ''}`}
                    onClick={() => handleSelectSource(source.id)}
                  >
                    <div className="source-checkbox">
                      {selectedData.includes(source.id) && <Check size={12} />}
                    </div>
                    <div className="source-info">
                      <span className="source-name">{source.name}</span>
                      <span className="source-count">{source.count} items</span>
                    </div>
                    <span className="source-type">{source.type}</span>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  No data sources found matching "{searchTerm}"
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Export Options */}
          <div className="export-options-panel">
            <div className="panel-header">
              <h3>Export Options</h3>
            </div>

            <div className="export-format-section">
              <label className="format-label">Export Format</label>
              <div className="format-dropdown-container" ref={dropdownRef}>
                <button 
                  className="format-selector"
                  onClick={() => setShowFormatDropdown(!showFormatDropdown)}
                  type="button"
                >
                  <span className="format-icon">{getFormatIcon(selectedFormat)}</span>
                  <span className="format-name">{selectedFormatName}</span>
                  <ChevronDown size={16} className={`dropdown-arrow ${showFormatDropdown ? 'rotated' : ''}`} />
                </button>

                {showFormatDropdown && (
                  <div className="format-dropdown">
                    {formats.map(format => (
                      <div
                        key={format.id}
                        className={`format-option ${selectedFormat === format.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFormat(format.id);
                          setShowFormatDropdown(false);
                        }}
                      >
                        <span className="format-option-icon">{format.icon}</span>
                        <div className="format-option-info">
                          <span className="format-option-name">{format.name}</span>
                          <span className="format-option-description">{format.description}</span>
                        </div>
                        {selectedFormat === format.id && <Check size={14} className="selected-check" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="export-summary">
              <div className="summary-item">
                <span className="summary-label">Items to export:</span>
                <span className="summary-value">{getSelectedCountText()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Export format:</span>
                <span className="summary-value">{selectedFormatName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Estimated size:</span>
                <span className="summary-value">{estimatedSize}</span>
              </div>
            </div>

            <div className="export-actions">
              <button 
                className="export-btn"
                onClick={handleExport}
                disabled={selectedData.length === 0 || isExporting}
                type="button"
              >
                {isExporting ? (
                  <>
                    <div className="spinner"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    <span>Export Data</span>
                  </>
                )}
              </button>

              {exportSuccess && (
                <div className="export-success">
                  <Check size={16} />
                  <span>Export completed successfully!</span>
                </div>
              )}
            </div>

            <div className="export-info">
              <h4>About Export Formats</h4>
              <ul className="format-info-list">
                <li><strong>PDF:</strong> Best for reports and documents with formatting</li>
                <li><strong>CSV:</strong> Ideal for spreadsheets and data analysis</li>
                <li><strong>JSON:</strong> Structured data for developers and APIs</li>
                <li><strong>Excel:</strong> Microsoft Excel compatible format</li>
                <li><strong>XML:</strong> Extensible markup for data exchange</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportData;